import { useState } from 'react'
import { useParams } from 'react-router'
import { CheckCircle2, Circle, Coins, FileCheck2, Play } from 'lucide-react'
import {
  DateValue, DetailTabs, ErrorState, FileUploader, FormDialog, PageHeader, StatusBadge,
  type UploadedFile,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useProCaseContext, useProCaseMutations } from '../api'
import { PRO_SOURCE_LABEL, PRO_STATUS_LABEL, PRO_STATUS_TONE } from '../types'
import { DatePicker } from '@/components/ui/date-picker'

/**
 * One case, worked.
 *
 * <p>Everything comes from one call, so the checklist never renders while the steps are still
 * loading — which is exactly when somebody completes a step that the checklist would have
 * blocked.
 */
export function ProCasePage() {
  const { id } = useParams<{ id: string }>()
  const breadcrumb = useBreadcrumb('Case')
  const context = useProCaseContext(id ?? null)
  const actions = useProCaseMutations()

  const [satisfying, setSatisfying] = useState<string | null>(null)
  const [itemFile, setItemFile] = useState<string | null>(null)
  const [itemExpiry, setItemExpiry] = useState('')
  const [itemNumber, setItemNumber] = useState('')
  const [costOpen, setCostOpen] = useState(false)
  const [cost, setCost] = useState({ description: '', amount: '', currency: 'AED' })
  const [completeOpen, setCompleteOpen] = useState(false)
  const [completion, setCompletion] = useState({ documentNumber: '', expiryDate: '', issueDate: '' })
  const [error, setError] = useState<string | null>(null)

  if (context.isPending) return <Skeleton className="h-96 w-full rounded-[var(--radius-card)]" />
  if (context.isError) return <ErrorState error={context.error} onRetry={() => void context.refetch()} />
  if (!context.data) return null

  const { proCase, steps, checklist, costs, outstandingChecklist } = context.data

  async function run(work: () => Promise<unknown>) {
    setError(null)
    try {
      await work()
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That could not be done')
    }
  }

  const checklistTab = (
    <div className="space-y-3">
      {outstandingChecklist.length > 0 && (
        <p className="rounded-[var(--radius-card)] bg-[var(--color-tint-orange)] p-3 text-sm text-[var(--color-warning)]">
          Still to gather: {outstandingChecklist.join(', ')}
        </p>
      )}
      <Card>
        <ul className="divide-y divide-[var(--color-border)]">
          {checklist.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-3 p-3 text-sm">
              {item.satisfied ? (
                <CheckCircle2 className="size-4 text-[var(--color-success)]" aria-hidden="true" />
              ) : (
                <Circle className="size-4 text-[var(--color-muted)]" aria-hidden="true" />
              )}
              <span className="min-w-0 flex-1 text-[var(--color-heading)]">
                {item.label}
                {!item.mandatory && (
                  <span className="ms-2 text-xs text-[var(--color-muted)]">optional</span>
                )}
                {item.employeeDocumentId && (
                  <span className="ms-2 text-xs text-[var(--color-success)]">
                    also filed on their record
                  </span>
                )}
              </span>
              {item.satisfied ? (
                <Button
                  type="button" size="sm" variant="ghost"
                  onClick={() => void run(() => actions.untickItem.mutateAsync(item.id))}
                >
                  Untick
                </Button>
              ) : (
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={() => {
                    setSatisfying(item.id)
                    setItemFile(null)
                    setItemExpiry('')
                    setItemNumber('')
                  }}
                >
                  Gathered
                </Button>
              )}
            </li>
          ))}
          {checklist.length === 0 && (
            <li className="p-3 text-sm text-[var(--color-muted)]">
              This process asks for nothing to be gathered.
            </li>
          )}
        </ul>
      </Card>
    </div>
  )

  const timelineTab = (
    <Card>
      <ol className="divide-y divide-[var(--color-border)]">
        {steps.map((step) => (
          <li key={step.id} className="flex flex-wrap items-center gap-3 p-3 text-sm">
            <span className="min-w-0 flex-1">
              <span className="font-medium text-[var(--color-heading)]">
                {step.seq}. {step.name}
              </span>
              <span className="ms-2 text-xs text-[var(--color-muted)]">
                {step.expectedDays != null && `expected ${step.expectedDays} d`}
                {step.actualDays != null && ` · took ${step.actualDays} d`}
                {step.referenceNo && ` · ${step.referenceNo}`}
              </span>
            </span>
            <StatusBadge status={step.status} />
            {step.status === 'PENDING' && (
              <Button
                type="button" size="sm" variant="ghost"
                onClick={() => void run(() => actions.startStep.mutateAsync(step.id))}
              >
                <Play className="size-3.5" aria-hidden="true" />
                Start
              </Button>
            )}
            {step.status !== 'DONE' && step.status !== 'SKIPPED' && (
              <Button
                type="button" size="sm" variant="outline"
                onClick={() => void run(() => actions.completeStep.mutateAsync({ stepId: step.id }))}
              >
                Done
              </Button>
            )}
          </li>
        ))}
      </ol>
    </Card>
  )

  const costsTab = (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button type="button" size="sm" onClick={() => { setError(null); setCostOpen(true) }}>
          <Coins className="size-4" aria-hidden="true" />
          Record a cost
        </Button>
      </div>
      <Card>
        <ul className="divide-y divide-[var(--color-border)]">
          {costs.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-3 p-3 text-sm">
              <span className="min-w-0 flex-1 text-[var(--color-heading)]">{row.description}</span>
              <span className="tabular-nums">{row.amount.toFixed(2)} {row.currency}</span>
              <span className="text-xs text-[var(--color-muted)]">
                <DateValue value={row.incurredOn} />
              </span>
            </li>
          ))}
          {costs.length === 0 && (
            <li className="p-3 text-sm text-[var(--color-muted)]">Nothing recorded yet.</li>
          )}
        </ul>
      </Card>
      {context.data.costSoFar != null && (
        <p className="text-sm tabular-nums text-[var(--color-heading)]">
          Total: {context.data.costSoFar.toFixed(2)} {context.data.costCurrency}
        </p>
      )}
      {context.data.costSoFar == null && costs.length > 0 && (
        <p className="text-sm text-[var(--color-muted)]">
          More than one currency, so these are not totalled — a sum across currencies looks
          like money and is not.
        </p>
      )}
    </div>
  )

  return (
    <div className="space-y-4">
      <PageHeader
        title={`${proCase.caseNo} · ${proCase.templateName}`}
        subtitle={`${proCase.employeeName ?? proCase.candidateRef ?? 'The company'} · ${PRO_SOURCE_LABEL[proCase.source]}`}
        breadcrumb={breadcrumb}
        actions={
          proCase.status !== 'COMPLETED' && proCase.status !== 'CANCELLED' ? (
            <Button type="button" onClick={() => { setError(null); setCompleteOpen(true) }}>
              <FileCheck2 className="size-4" aria-hidden="true" />
              Complete the case
            </Button>
          ) : null
        }
      />

      <Card className="flex flex-wrap items-center gap-4 p-4 text-sm">
        <StatusBadge
          status={proCase.status}
          tone={PRO_STATUS_TONE[proCase.status]}
          label={PRO_STATUS_LABEL[proCase.status]}
        />
        <span className="text-[var(--color-muted)]">
          Opened <DateValue value={proCase.openedOn} />
        </span>
        {proCase.targetDate && (
          <span className={proCase.overdue ? 'font-medium text-[var(--color-danger)]' : 'text-[var(--color-muted)]'}>
            Due <DateValue value={proCase.targetDate} />{proCase.overdue && ' — overdue'}
          </span>
        )}
      </Card>

      {error && <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>}

      <DetailTabs
        tabs={[
          { key: 'checklist', label: 'Checklist', badge: outstandingChecklist.length, content: checklistTab },
          { key: 'timeline', label: 'Timeline', content: timelineTab },
          { key: 'costs', label: 'Costs', content: costsTab },
          {
            key: 'notes',
            label: 'Notes',
            content: (
              <Card className="p-4 text-sm text-[var(--color-muted)]">
                {proCase.notes ?? 'Nothing recorded against this case.'}
              </Card>
            ),
          },
        ]}
      />

      <FormDialog
        open={satisfying != null}
        onOpenChange={(next) => !next && setSatisfying(null)}
        title="Mark as gathered"
        description="A document upload is also filed on the person's own record, so the expiry sweep can watch it."
        submitLabel="Gathered"
        loading={actions.satisfyItem.isPending}
        error={error}
        onSubmit={() =>
          void run(async () => {
            await actions.satisfyItem.mutateAsync({
              itemId: satisfying!,
              storedFileId: itemFile,
              expiryDate: itemExpiry || null,
              documentNumber: itemNumber || null,
            })
            setSatisfying(null)
          })
        }
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Upload (optional for items that are not documents)</Label>
            <FileUploader
              ownerType="pro_case_checklist_item"
              onUploaded={(file: UploadedFile) => setItemFile(file.id)}
              onRemoved={() => setItemFile(null)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="item-number">Document number</Label>
              <Input id="item-number" value={itemNumber}
                     onChange={(event) => setItemNumber(event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="item-expiry">Expires</Label>
              <DatePicker id="item-expiry" value={itemExpiry}
                     onChange={(event) => setItemExpiry(event.target.value)} />
              <p className="text-xs text-[var(--color-muted)]">
                Needed before it can be filed as a document.
              </p>
            </div>
          </div>
        </div>
      </FormDialog>

      <FormDialog
        open={costOpen}
        onOpenChange={setCostOpen}
        title="Record a cost"
        loading={actions.addCost.isPending}
        error={error}
        onSubmit={() =>
          void run(async () => {
            await actions.addCost.mutateAsync({
              id: id!,
              description: cost.description,
              amount: Number(cost.amount),
              currency: cost.currency,
            })
            setCostOpen(false)
            setCost({ description: '', amount: '', currency: 'AED' })
          })
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="cost-description">What was it for</Label>
            <Input id="cost-description" required value={cost.description}
                   placeholder="Typing centre, government fee, courier…"
                   onChange={(event) => setCost({ ...cost, description: event.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cost-amount">Amount</Label>
            <Input id="cost-amount" type="number" min={0} step="0.01" required value={cost.amount}
                   onChange={(event) => setCost({ ...cost, amount: event.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cost-currency">Currency</Label>
            <Input id="cost-currency" required maxLength={3} value={cost.currency}
                   onChange={(event) => setCost({ ...cost, currency: event.target.value })} />
          </div>
        </div>
      </FormDialog>

      <FormDialog
        open={completeOpen}
        onOpenChange={setCompleteOpen}
        title="Complete the case"
        description="Enter what you read off the document. Nothing here is inferred — a guessed expiry would block placements later."
        submitLabel="Complete"
        loading={actions.complete.isPending}
        error={error}
        onSubmit={() =>
          void run(async () => {
            await actions.complete.mutateAsync({
              id: id!,
              documentNumber: completion.documentNumber || null,
              issueDate: completion.issueDate || null,
              expiryDate: completion.expiryDate || null,
            })
            setCompleteOpen(false)
          })
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="done-number">Document number</Label>
            <Input id="done-number" value={completion.documentNumber}
                   onChange={(event) => setCompletion({ ...completion, documentNumber: event.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="done-issued">Issued</Label>
            <DatePicker id="done-issued" value={completion.issueDate}
                   onChange={(event) => setCompletion({ ...completion, issueDate: event.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="done-expiry">Expires</Label>
            <DatePicker id="done-expiry" value={completion.expiryDate}
                   onChange={(event) => setCompletion({ ...completion, expiryDate: event.target.value })} />
          </div>
        </div>
      </FormDialog>
    </div>
  )
}
